// @ts-strict-ignore
import React, { useMemo } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Avatar, Box, Callout, Combobox } from '@node-space/ui-components'
import { useBulkCapabiltiesQuery } from 'hooks/queries/Capabilities/useCapabiltiesQuery'
import { useWalletsQueryV2 } from 'hooks/queries/useWalletsQueryV2'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { useSupportedCurrencies } from 'hooks/useSupportedCurrencies'
import { BeneficiaryTransferDestination } from 'types/beneficiaries'
import { BulkCapabilityRequest } from 'types/capabilities/capabilities'
import { Currencies, WalletTypes } from 'types/types'
import { getWalletActionCapabilityRequestFilters } from 'utils/capabilities'
import { roundNumberWithCommas } from 'utils/utils'
import { getWalletPropertiesForCapabilityChecks } from 'utils/wallet'
import { getFiatWalletActionSupport } from 'utils/wallets/fiat'
import { merchantAtom, walletAtom, walletTypeAtom } from '../atoms/massPayoutAtoms'

const MASS_PAYOUTS_FIAT_CURRENCIES: string[] = [Currencies.GBP, Currencies.EUR]

export const MassPayoutWalletSelection = () => {
  const { enableFiatCurrencyFilter, enableFiatPayinCapabilityV2, enableFiatPayoutCapabilityV2 } =
    useFeatureFlags()
  const { getSupportedCurrencyWallets, getWalletOptions } = useSupportedCurrencies()
  const permissions = useMappedRoles()
  const canEdit = permissions?.massPayouts?.canEdit
  const { t } = useTranslation()
  const [merchant, setMerchant] = useAtom(merchantAtom)
  const [wallet, setWallet] = useAtom(walletAtom)
  const walletType = useAtomValue(walletTypeAtom)
  const isFiatMasspayouts = walletType === WalletTypes.FIAT

  const {
    data: wallets,
    isFetching: isFetchingWallets,
    isError: isErrorWallets,
  } = useWalletsQueryV2({
    walletType: '',
    max: 100,
  })

  const cryptoWallets = getSupportedCurrencyWallets(wallets?.data, true)
  const cryptoWalletsOptions = getWalletOptions(cryptoWallets, true)

  const fiatFilteredWallets = useMemo(() => {
    return isFiatMasspayouts
      ? wallets?.data?.filter(wallet => {
          const isInternationalPaymentSupportedOnly =
            wallet?.supportedTransferDestinations?.length === 1 &&
            wallet?.supportedTransferDestinations?.includes(
              BeneficiaryTransferDestination.INTERNATIONAL
            )

          return (
            wallet?.currency?.fiat &&
            MASS_PAYOUTS_FIAT_CURRENCIES.includes(wallet?.currency?.code) &&
            !!wallet?.supportedTransferDestinations?.length &&
            !isInternationalPaymentSupportedOnly
          )
        })
      : []
  }, [wallets, isFiatMasspayouts])

  const fiatWalletCapabilitiesRequest: BulkCapabilityRequest = useMemo(() => {
    if (isFiatMasspayouts && !!fiatFilteredWallets?.length) {
      const walletLsids = fiatFilteredWallets?.map(wallet => wallet?.lsid)

      return getWalletActionCapabilityRequestFilters(
        walletLsids,
        enableFiatPayinCapabilityV2,
        enableFiatPayoutCapabilityV2
      )
    }
  }, [isFiatMasspayouts, fiatFilteredWallets])

  const { data: bulkCapabilities, isFetching: isFetchingWalletsCapabilities } =
    useBulkCapabiltiesQuery(fiatWalletCapabilitiesRequest, !!fiatWalletCapabilitiesRequest)

  const fiatWallets = fiatFilteredWallets?.filter(wallet => {
    const walletProperties = getWalletPropertiesForCapabilityChecks(wallet)

    const { supportsPay, supportsWithdrawals } = getFiatWalletActionSupport({
      walletProperties,
      bulkCapabilities,
      enableFiatCurrencyFilter,
    })

    return supportsPay || supportsWithdrawals
  })

  const fiatWalletsOptions = fiatWallets?.map(wallet => {
    const code = wallet?.currency?.code
    const approximateBalance = roundNumberWithCommas(wallet?.approxBalance, 2)
    return {
      rightText: ` ${approximateBalance} ${code}`,
      LeftElement: <Avatar name={code.toLowerCase()} size="small" />,
      leftText: wallet?.description,
      value: wallet?.lsid,
    }
  })

  const onWalletChangeHandler = (value: string) => {
    setWallet(value)
  }

  const onMerchantChangeHandler = (value: string) => {
    const walletDescription = (cryptoWalletsOptions?.find(options => options.value === value)
      ?.leftText ?? '') as string
    setMerchant(value)
    setWallet(walletDescription)
  }
  const isFiat = walletType === WalletTypes.FIAT
  const selectedWallet = fiatWallets?.find(fiatWallet => fiatWallet.lsid === wallet)
  const isVopDisclaimerRequired = isFiat && selectedWallet?.currency?.code === Currencies.EUR

  return (
    <Box flexCol gapY={4} className="justify-start">
      {isFiatMasspayouts ? (
        <Combobox
          id="select-fiat-wallet"
          label={t('massPayouts.selectWalletToPayFrom')}
          items={fiatWalletsOptions}
          loading={isFetchingWallets || isFetchingWalletsCapabilities}
          disabled={!canEdit}
          onChange={value => {
            onWalletChangeHandler(value)
          }}
          searchPlaceholder={t('massPayouts.searchOrSelect')}
          defaultValue={wallet}
          rightHeader={t('fundingWallet')}
        />
      ) : (
        <Combobox
          id="select-fiat-wallet"
          label={t('massPayouts.selectWalletToPayFrom')}
          items={cryptoWalletsOptions}
          disabled={!canEdit}
          loading={isFetchingWallets}
          onChange={value => {
            onMerchantChangeHandler(value)
          }}
          searchPlaceholder={t('massPayouts.searchOrSelect')}
          defaultValue={merchant}
          rightHeader={t('fundingWallet')}
        />
      )}
      {isFiatMasspayouts ? (
        <>
          <Box paddingT={16}>
            {isVopDisclaimerRequired && (
              <Callout variant="warning">{t('fiatMassPayoutsVOPDisclaimer')}</Callout>
            )}
          </Box>
          <Box paddingT={24}>
            <Callout variant="info">{t('massPayouts.selectWalletDescriptionFiat')}</Callout>
          </Box>
        </>
      ) : (
        <p className="text-body-regular-small text-foreground-subdued">
          {t('massPayouts.selectWalletDescription')}
        </p>
      )}
    </Box>
  )
}
